import React from "react";
import {
  Row,
  Col,
  Select,
  DatePicker,
  TimePicker,
  Checkbox,
  InputNumber,
  Input,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const FREQUENCIES = [
  { value: "ONCE", label: "One-time (a date)" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKDAYS", label: "Workdays (Mon–Fri)" },
  { value: "WEEKLY", label: "Weekly (choose days)" },
  { value: "MONTHLY", label: "Monthly (day of month)" },
];

const WEEKDAYS = [
  { label: "Mon", value: "MON" },
  { label: "Tue", value: "TUE" },
  { label: "Wed", value: "WED" },
  { label: "Thu", value: "THU" },
  { label: "Fri", value: "FRI" },
  { label: "Sat", value: "SAT" },
  { label: "Sun", value: "SUN" },
];

export const INITIAL_SCHEDULE = {
  frequency: "DAILY",
  runDate: null, // dayjs
  days: [], // ["MON", ...]
  dayOfMonth: 1,
  startTime: null, // dayjs
  endTime: null, // dayjs
  password: "",
};

// Per-frequency validators (config-driven, no if/else chains).
const VALIDATORS = {
  ONCE: (s) => (!s.runDate ? "Pick a date for the one-time run" : null),
  WEEKLY: (s) =>
    !s.days || s.days.length === 0 ? "Pick at least one day of the week" : null,
  MONTHLY: (s) =>
    !s.dayOfMonth || s.dayOfMonth < 1 || s.dayOfMonth > 31
      ? "Day of month must be 1–31"
      : null,
};

export function validateSchedule(s, { requirePassword = true } = {}) {
  if (!s.startTime) return "Start time is required";
  if (!s.endTime) return "End time is required";
  if (requirePassword && !s.password) return "A control password is required";
  const validate = VALIDATORS[s.frequency];
  return validate ? validate(s) : null;
}

// Build the backend payload (formats dayjs values to the strings the API expects).
export function buildSchedulePayload(s, defaultEnvironment, msName) {
  return {
    msName,
    defaultEnvironment,
    frequency: s.frequency,
    runDate:
      s.frequency === "ONCE" && s.runDate ? s.runDate.format("DD-MM-YYYY") : null,
    daysOfWeek: s.frequency === "WEEKLY" ? (s.days || []).join(",") : null,
    dayOfMonth: s.frequency === "MONTHLY" ? s.dayOfMonth : null,
    startTime: s.startTime ? s.startTime.format("HH:mm") : null,
    endTime: s.endTime ? s.endTime.format("HH:mm") : null,
    ...(s.password ? { password: s.password } : {}),
  };
}

// Hydrate form state from a stored schedule DTO (used by the edit flow).
export function scheduleFromDto(dto) {
  return {
    frequency: dto.frequency || "DAILY",
    runDate: dto.runDate ? dayjs(dto.runDate, "DD-MM-YYYY") : null,
    days: dto.daysOfWeek ? dto.daysOfWeek.split(",").filter(Boolean) : [],
    dayOfMonth: dto.dayOfMonth || 1,
    startTime: dto.startTime ? dayjs(dto.startTime, "HH:mm") : null,
    endTime: dto.endTime ? dayjs(dto.endTime, "HH:mm") : null,
    password: "",
  };
}

function FieldRow({ label, children }) {
  return (
    <Row style={{ paddingTop: 16 }} align="middle">
      <Col span={6}>
        <p className="spec-modal-label">{label}</p>
      </Col>
      <Col span={18}>{children}</Col>
    </Row>
  );
}

function ScheduleForm({ value, onChange, showPassword = true }) {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <>
      <FieldRow label="Frequency">
        <Select
          value={value.frequency}
          options={FREQUENCIES}
          onChange={(f) => set({ frequency: f })}
          style={{ width: "100%" }}
        />
      </FieldRow>

      {value.frequency === "ONCE" && (
        <FieldRow label="Date">
          <DatePicker
            value={value.runDate}
            onChange={(d) => set({ runDate: d })}
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
          />
        </FieldRow>
      )}

      {value.frequency === "WEEKLY" && (
        <FieldRow label="Days">
          <Checkbox.Group
            options={WEEKDAYS}
            value={value.days}
            onChange={(d) => set({ days: d })}
          />
        </FieldRow>
      )}

      {value.frequency === "MONTHLY" && (
        <FieldRow label="Day of month">
          <InputNumber
            min={1}
            max={31}
            value={value.dayOfMonth}
            onChange={(n) => set({ dayOfMonth: n })}
          />
        </FieldRow>
      )}

      <FieldRow label="Start time">
        <TimePicker
          value={value.startTime}
          onChange={(t) => set({ startTime: t })}
          format="HH:mm"
          minuteStep={5}
          style={{ width: "100%" }}
        />
      </FieldRow>

      <FieldRow label="End time (cutoff)">
        <TimePicker
          value={value.endTime}
          onChange={(t) => set({ endTime: t })}
          format="HH:mm"
          minuteStep={5}
          style={{ width: "100%" }}
        />
      </FieldRow>

      {showPassword && (
        <FieldRow label="Control password">
          <Input.Password
            value={value.password}
            onChange={(e) => set({ password: e.target.value })}
            placeholder="set a password to control this schedule"
          />
        </FieldRow>
      )}
    </>
  );
}

export default ScheduleForm;
